'use client';

/**
 * Portal file dialog — the ONE host-side file dialog behind `host.content.pick`
 * (open), `host.documents.saveDialog` (save a copy), AND the "Move to…" action
 * in the content / workspaces panels (move).
 *
 * All three modes share the SAME chrome (a glass-panel `PopoverPortal` sheet,
 * matching ShareDialog) and the SAME browser: breadcrumbs from My files (the
 * content root, where workspaces live as folders), a folders+files listing per
 * level, and a flat search across the library. Mode only changes the action:
 *
 *   - `open`: files matching `accept` are selectable (click / double-click,
 *     multi), plus Upload-from-computer and a Link/URL tab when `kinds` allows.
 *   - `save`: a name field + Save here confirming the CURRENT folder as the
 *     destination; files shown muted for context.
 *   - `move`: Move here confirms the CURRENT folder as the destination for an
 *     item being moved (excluded from the listing); files shown muted.
 *
 * Security model unchanged: trusted portal chrome rendered OVER the canvas. The
 * widget only learns the items the user picked (open) or the destination the
 * user confirmed (save / move).
 */

import { trackUpload } from '@/lib/content/transfers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import {
  X,
  Link as LinkIcon,
  Search,
  Upload,
  FolderInput,
  FolderPlus,
  ChevronRight,
  Home,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { cn } from '@/lib/utils';
import { WORKSPACE_PROJECT_MIME } from '@/lib/workspaces/types';
import { resolveContentKind } from '@/lib/content/app-identity';
import { ContentPreview } from '@/components/workspace/panels/content-preview';

type PickKind = 'file' | 'url';

export interface PickedItem {
  kind: PickKind;
  id?: string;
  name?: string;
  mime?: string;
  size?: number;
  url?: string;
}

interface ContentRow {
  id: string;
  displayName: string;
  originalName: string;
  mimeType: string;
  size: number;
  contentType: string;
  metadata?: Record<string, unknown> | null;
}

/** ADR-0022: a PROJECT folder (an app's save) declares its open-with mime in
 *  `metadata.projectMime` — that folder IS the app's document. */
function projectMimeOf(it: ContentRow): string | null {
  if (it.contentType !== 'folder') return null;
  const m = it.metadata?.projectMime;
  return typeof m === 'string' && m ? m : null;
}

export type PortalFileDialogProps =
  | {
      mode: 'open';
      accept?: string[];
      multi?: boolean;
      kinds?: PickKind[];
      onResolve: (items: PickedItem[] | null) => void;
    }
  | {
      mode: 'save';
      title?: string;
      defaultName?: string;
      /** Starting folder (e.g. the current workspace). Root is always reachable above it. */
      initialFolderId?: string | null;
      onResolve: (result: { parentId: string | null; name: string } | null) => void;
    }
  | {
      mode: 'move';
      title?: string;
      /** The item being moved — excluded from the listing so it can't move into itself. */
      excludeId?: string;
      /** Bulk move: every selected id is excluded from the destination listing. */
      excludeIds?: string[];
      initialFolderId?: string | null;
      onResolve: (result: { parentId: string | null } | null) => void;
    };

function mimeMatches(mime: string, accept?: string[]): boolean {
  if (!accept || !accept.length) return true;
  return (
    accept.some((a) => {
      if (a.endsWith('/*')) return mime.startsWith(a.slice(0, -1));
      if (a.startsWith('.')) return false; // extension filters match on names below
      return mime === a;
    }) || false
  );
}

function itemMatches(it: ContentRow, accept?: string[]): boolean {
  if (!accept || !accept.length) return true;
  if (mimeMatches(it.mimeType, accept)) return true;
  const name = (it.originalName || it.displayName || '').toLowerCase();
  return accept.some((a) => a.startsWith('.') && name.endsWith(a.toLowerCase()));
}

function fmtSize(n: number): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Shared pill (tab / scope chip) — active = the neutral "selected" treatment
// used across the system dialogs, not an ad-hoc accent.
function pillCls(active: boolean): string {
  return cn(
    'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
    active ? 'bg-foreground text-background' : 'bg-foreground/8 hover:bg-foreground/12',
  );
}

export function PortalFileDialog(props: PortalFileDialogProps) {
  const t = useT();
  const isOpenMode = props.mode === 'open';
  const isSaveMode = props.mode === 'save';
  const isMoveMode = props.mode === 'move';
  const accept = isOpenMode ? props.accept : undefined;
  const hasAccept = !!(accept && accept.length);
  // String form for the listing query + effect deps (the array is a fresh
  // reference on every render).
  const acceptKey = accept?.join(',') ?? '';
  const excludeId = isMoveMode ? props.excludeId : undefined;
  // One membership set for single + bulk moves; every excluded row is both
  // unlistable and undescendable, so a selection can't move into itself.
  const excludedIds = useMemo(() => {
    const s = new Set<string>();
    if (isMoveMode) {
      if (props.excludeId) s.add(props.excludeId);
      for (const id of props.excludeIds ?? []) s.add(id);
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoveMode, excludeId, isMoveMode ? props.excludeIds : undefined]);
  // Only OPEN lets you select files; save/move show them muted for context.
  const selectableFiles = isOpenMode;

  // ---- navigation: breadcrumb stack rooted at My files -------------------
  const [stack, setStack] = useState<{ id: string | null; name: string }[]>([
    { id: null, name: t('workspace.files.myFiles') },
  ]);
  const current = stack[stack.length - 1];

  // Save / move: seed the stack with the starting folder (the workspace) UNDER
  // the root crumb, so "up to root" is always one click. Name comes from /info.
  const initialFolderId = isSaveMode || isMoveMode ? props.initialFolderId : null;
  useEffect(() => {
    if (!initialFolderId) return;
    const id = initialFolderId;
    let dead = false;
    (async () => {
      let name = 'Workspace';
      try {
        const r = await fetch(`/api/content/${encodeURIComponent(id)}/info`, { credentials: 'same-origin' });
        if (r.ok) {
          const data = (await r.json()) as { displayName?: string };
          if (data.displayName) name = data.displayName;
        }
      } catch {
        // keep the placeholder name — navigation still works
      }
      if (!dead) setStack([{ id: null, name: t('workspace.files.myFiles') }, { id, name }]);
    })();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- listing: browse the current folder, or flat-search the library ------
  const [rows, setRows] = useState<ContentRow[]>([]);
  const [loading, setLoading] = useState(false);
  // Bumped after a failed upload: a multi-file upload can error partway with
  // earlier files already saved, so re-list to show whatever DID land.
  const [refreshTick, setRefreshTick] = useState(0);
  const [q, setQ] = useState('');
  const searching = !!q.trim();
  // Open-mode file scope: For this app / All files / Shared with me.
  // Declared here (not with the other open-mode state below) because `appFlat`
  // + its effect read it. Shared with me is other people's content you were
  // granted, which lives outside your own folder tree.
  const [scope, setScope] = useState<'widget' | 'all' | 'shared'>(hasAccept ? 'widget' : 'all');
  const sharedScope = isOpenMode && scope === 'shared';

  // For this app at the root = a FLAT, cross-folder view of the files this app
  // can open (rather than the library root, which is all folders and looks
  // identical to All files). Omitting parentId flattens the tree;
  // excludeBundleFiles drops widget-bundle / project-folder INTERNALS so the
  // feed is just the user's real documents.
  const appFlat = isOpenMode && scope === 'widget' && hasAccept && !searching && current.id === null;
  useEffect(() => {
    let dead = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        // Shared scope: the top level lists the grants themselves, and browsing
        // into a shared folder needs `any` so the server resolves reachability
        // through the folder rather than expecting a grant per child.
        const owner = sharedScope ? (current.id === null ? 'shared' : 'any') : 'me';
        const params = new URLSearchParams({ owner, limit: searching ? '60' : '200' });
        // Parity with the Content panel: hidden rows (seeded widget bundles,
        // backing functions) never belong in a picker, whatever the mode.
        params.set('excludeBundleFiles', '1');
        if (searching) {
          params.set('q', q.trim());
          params.set('sort', 'recent');
        } else if (appFlat) {
          params.set('sort', 'newest');
          // Filter on the server, BEFORE the page limit. Filtering the newest
          // page client-side hid every app document older than the newest
          // library rows until you searched (the FPGA Reach projects,
          // 2026-09-25). The client filter below still runs; it is now a no-op.
          params.set('accept', acceptKey);
        } else if (sharedScope && current.id === null) {
          // Shared items sit in someone else's tree, never at your root.
          params.set('sort', 'newest');
        } else {
          // Browse: the SAME defaults as the Content panel (folders first,
          // recently modified within) — the two lists must read identically.
          params.set('parentId', current.id ?? 'null');
          params.set('sort', 'recent');
          params.set('foldersFirst', '1');
        }
        const r = await fetch(`/api/content?${params}`, { credentials: 'same-origin' });
        const data = r.ok ? ((await r.json()) as { items?: ContentRow[] }) : { items: [] };
        if (!dead) setRows(data.items ?? []);
      } catch {
        if (!dead) setRows([]);
      } finally {
        if (!dead) setLoading(false);
      }
    }, searching ? 200 : 0);
    return () => {
      dead = true;
      clearTimeout(t);
    };
  }, [q, searching, current.id, appFlat, sharedScope, refreshTick, acceptKey]);

  // Scopes browse different trees, so switching resets to that scope's root
  // instead of carrying a stale breadcrumb across.
  const switchScope = useCallback((next: 'widget' | 'all' | 'shared') => {
    setScope(next);
    setQ('');
    setStack((s) => s.slice(0, 1));
  }, []);

  const goTo = useCallback((idx: number) => setStack((s) => s.slice(0, idx + 1)), []);
  const enter = useCallback((f: ContentRow) => {
    setQ('');
    setStack((s) => [...s, { id: f.id, name: f.displayName }]);
  }, []);

  // ---- open-mode state ------------------------------------------------------
  const [tab, setTab] = useState<PickKind>('file');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [urlValue, setUrlValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadNames, setUploadNames] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpenMode) return;
    const kinds = props.kinds;
    if (kinds && kinds.length && !kinds.includes('file')) setTab('url');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- save-mode state ------------------------------------------------------
  const [name, setName] = useState(isSaveMode ? props.defaultName || 'Untitled' : '');

  // ---- inline New folder (move + save destinations) -----------------------
  // Half of all moves are "into a folder that does not exist yet". Creating a
  // sibling with a taken name gets a quiet nudge; storage stays permissive.
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const nameTaken =
    newFolderOpen &&
    !!newFolderName.trim() &&
    rows.some(
      (r) =>
        r.contentType === 'folder' &&
        r.displayName.trim().toLowerCase() === newFolderName.trim().toLowerCase(),
    );
  const createFolderHere = useCallback(async () => {
    const folderName = newFolderName.trim();
    if (!folderName || creatingFolder) return;
    setCreatingFolder(true);
    try {
      const r = await fetch('/api/content/folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ displayName: folderName, parentId: current.id }),
      });
      const data = (await r.json().catch(() => null)) as { id?: string } | null;
      setNewFolderOpen(false);
      setNewFolderName('');
      if (r.ok && data?.id) {
        // Enter the fresh folder — the confirm buttons act on the CURRENT
        // folder, so "New folder → Move here" is two clicks.
        setStack((s) => [...s, { id: data.id as string, name: folderName }]);
      } else {
        setRefreshTick((n) => n + 1);
      }
    } finally {
      setCreatingFolder(false);
    }
  }, [newFolderName, creatingFolder, current.id]);

  const cancel = useCallback(() => {
    // Every mode's onResolve accepts null; narrow per-mode to satisfy the union.
    if (props.mode === 'open') props.onResolve(null);
    else if (props.mode === 'save') props.onResolve(null);
    else props.onResolve(null);
  }, [props]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [cancel]);

  // Most app saves are PROJECT FOLDERS (ADR-0022), not plain files — in the
  // For this app scope a folder whose `metadata.projectMime` matches `accept`
  // is a PICKABLE document row, not navigation. Without this the scope was
  // empty for every folder-backed app (vector/video/EDA/docs): the flat feed
  // hides folders and the mime filter can never match a folder's mimeType.
  // The same rule has to hold in the SHARED scope: a notebook (or any
  // folder-backed document) that a teammate shared is exactly the thing the
  // person came here to open, and without this it was navigation only — you
  // could walk into someone's shared document but never pick it.
  const projectIds = new Set(
    isOpenMode && (scope === 'widget' || sharedScope)
      ? rows
          .filter((r) => {
            if (excludedIds.has(r.id)) return false;
            const pm = projectMimeOf(r);
            return !!pm && mimeMatches(pm, accept);
          })
          .map((r) => r.id)
      : [],
  );
  // In the flat For this app view we hide folders (it's a cross-folder file
  // list, not a tree to browse); otherwise folders are shown for navigation.
  const folders = appFlat ? [] : rows.filter((r) => r.contentType === 'folder' && !excludedIds.has(r.id) && !projectIds.has(r.id));
  const allFiles = rows.filter((r) => r.contentType !== 'folder' && !excludedIds.has(r.id));
  // Move mode: an app DOCUMENT (project folder) is a container for its app,
  // not for the user — navigable in open mode, but never a move destination,
  // so it renders disabled with the reason instead of inviting a descent.
  const isAppDocFolder = (r: ContentRow): boolean => {
    if (!isMoveMode || r.contentType !== 'folder') return false;
    const pm = r.metadata?.projectMime;
    return typeof pm === 'string' && !!pm && pm !== WORKSPACE_PROJECT_MIME;
  };
  const files =
    isOpenMode && scope === 'widget'
      ? rows.filter((r) => projectIds.has(r.id) || (r.contentType !== 'folder' && !excludedIds.has(r.id) && itemMatches(r, accept)))
      : sharedScope
        ? [...rows.filter((r) => projectIds.has(r.id)), ...allFiles]
        : allFiles;

  const toggle = (id: string) => {
    if (!isOpenMode) return;
    const multi = props.mode === 'open' && props.multi;
    setSel((prev) => {
      const next = new Set(multi ? prev : []);
      if (prev.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmOpen = () => {
    if (props.mode !== 'open') return;
    if (tab === 'url') {
      const url = urlValue.trim();
      if (!url || !/^https?:\/\//i.test(url)) return;
      props.onResolve([{ kind: 'url', url }]);
      return;
    }
    const picked = files
      .filter((it) => sel.has(it.id))
      .map((it) => ({
        kind: 'file' as const,
        id: it.id,
        name: it.displayName || it.originalName,
        // a project folder's document mime is its projectMime, not the folder mime
        mime: projectMimeOf(it) ?? it.mimeType,
        size: it.size,
      }));
    if (picked.length) props.onResolve(picked);
  };

  const confirmSave = () => {
    if (props.mode !== 'save') return;
    const v = name.trim();
    if (!v) return;
    props.onResolve({ parentId: current.id, name: v });
  };

  const confirmMove = () => {
    if (props.mode !== 'move') return;
    props.onResolve({ parentId: current.id });
  };

  // Upload from the PC (open mode): lands in the CURRENT folder (parentId) via
  // the library upload route, then resolves the pick with the uploaded items.
  // Response shape varies: ONE file comes back as the bare item, several come
  // back wrapped in { items } — parse both, or the single-file case (the common
  // one) resolves to nothing and the dialog hangs on Uploading… forever.
  const onUploadFiles = async (fl: FileList | null) => {
    if (props.mode !== 'open' || !fl || !fl.length || uploading) return;
    const picked = Array.from(fl).slice(0, props.multi ? 10 : 1);
    setUploading(true);
    setUploadError(null);
    setUploadNames(picked.map((f) => f.name));
    try {
      // Resumable, with progress in the transfer tray (CASFS-UI PF-4).
      const items = await Promise.all(
        picked.map((f) => trackUpload(f, { filename: f.name, mimeType: f.type, parentId: current.id ?? null, visibility: 'private' })),
      );
      const uploaded = items.map((it) => ({
        kind: 'file' as const,
        id: it.id,
        name: it.displayName,
        mime: it.mimeType,
        size: it.size,
      }));
      if (!uploaded.length) throw new Error('Upload failed. Please try again.');
      props.onResolve(uploaded);
    } catch (e) {
      setUploading(false);
      setUploadNames([]);
      setUploadError(e instanceof Error && e.message ? e.message : 'Upload failed. Please try again.');
      setRefreshTick((n) => n + 1);
    }
  };

  const kinds: PickKind[] = props.mode === 'open' && props.kinds && props.kinds.length ? props.kinds : ['file'];
  const showUrlTab = props.mode === 'open' && kinds.includes('url');
  const eyebrow = isSaveMode ? 'Save to' : isMoveMode ? 'Move to' : null;
  const headTitle = isSaveMode
    ? props.title || 'Save a copy'
    : isMoveMode
      ? props.title || 'Move item'
      : 'Choose from your files';

  const rowBtn = 'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors';

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={headTitle}
      // The panel's place comes from the classes below (a bottom sheet on a
      // phone, a card near the top on a desktop). The alignment was inline
      // too, and an inline style beats `md:items-start`, so on a desktop the
      // dialog sat at the bottom of the page (components/sharing/
      // share-dialog.tsx has the finding, 2026-10-02).
      style={{ position: 'fixed', inset: 0, zIndex: 240, display: 'flex', justifyContent: 'center' }}
      className="items-end bg-black/40 backdrop-blur-sm md:items-start md:py-[10vh]"
    >
      {/* Backdrop click closes (the panel stops propagation) */}
      <div aria-hidden className="absolute inset-0" onClick={cancel} />
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'glass-panel relative flex w-full flex-col overflow-hidden shadow-2xl',
          'max-h-[90vh] rounded-t-2xl',
          'md:max-h-[80vh] md:w-[min(480px,96vw)] md:rounded-2xl',
        )}
      >
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <div className="min-w-0">
            {eyebrow && <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{eyebrow}</div>}
            <div className="truncate text-sm font-semibold tracking-tight">{headTitle}</div>
          </div>
          <div className="ml-auto" />
          <button
            type="button"
            aria-label={t('workspace.canvas.node.close')}
            onClick={cancel}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {showUrlTab && (
          <div className="flex gap-1 px-4 pt-3">
            {(['file', 'url'] as PickKind[])
              .filter((k) => kinds.includes(k))
              .map((k) => (
                <button key={k} type="button" onClick={() => setTab(k)} className={pillCls(tab === k)}>
                  {k === 'file' ? t('workspace.files.myFiles') : 'Link / URL'}
                </button>
              ))}
          </div>
        )}

        {tab === 'file' ? (
          <>
            {isSaveMode && (
              <div className="px-4 pt-3">
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmSave();
                    }
                  }}
                  placeholder={t('workspace.node.name')}
                  maxLength={80}
                  className="w-full rounded-lg bg-foreground/5 px-3 py-2 text-sm outline-none ring-1 ring-border focus:ring-foreground/30"
                />
              </div>
            )}

            <div className="px-4 pt-3">
              <div className="flex items-center gap-2 rounded-lg bg-foreground/5 px-2 py-1.5 ring-1 ring-border">
                <Search className="h-3.5 w-3.5 opacity-50" />
                <input
                  autoFocus={isOpenMode}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('workspace.files.searchYourFiles')}
                  className="w-full bg-transparent text-sm outline-none"
                />
              </div>
              {/* Wraps rather than overflowing: the scope row now carries up to
                  three pills plus Upload in a narrow dialog. */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {/* Breadcrumbs — hidden while a search is active (search is library-wide) */}
                {!searching && (
                  <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto text-[11px] text-muted-foreground">
                    {stack.map((c, i) => (
                      <span key={`${c.id ?? 'root'}-${i}`} className="inline-flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => goTo(i)}
                          className={cn(
                            'inline-flex max-w-[130px] items-center gap-1 truncate transition-colors hover:text-foreground',
                            i === stack.length - 1 && 'font-semibold text-foreground',
                          )}
                        >
                          {i === 0 && <Home className="h-3 w-3" />}
                          {c.name}
                        </button>
                        {i < stack.length - 1 && <ChevronRight className="h-3 w-3 opacity-50" />}
                      </span>
                    ))}
                  </div>
                )}
                {searching && <span className="flex-1 text-[11px] text-muted-foreground">Search results across your files</span>}
                {isOpenMode && (
                  <>
                    {hasAccept && (
                      <button type="button" onClick={() => switchScope('widget')} className={pillCls(scope === 'widget')}>
                        {t('workspace.files.forThisApp')}
                      </button>
                    )}
                    <button type="button" onClick={() => switchScope('all')} className={pillCls(scope === 'all')}>
                      {t('workspace.files.allFiles')}
                    </button>
                    <button type="button" onClick={() => switchScope('shared')} className={pillCls(scope === 'shared')}>
                      {t('workspace.panels.sharedWithMe')}
                    </button>
                  </>
                )}
                {/* Uploads land in YOUR library, so the button is off in the
                    shared scope where the user isn't looking at their own tree. */}
                {isOpenMode && !sharedScope && (
                  <>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex shrink-0 items-center gap-1.5 rounded-full bg-foreground/8 px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-foreground/12 disabled:opacity-50"
                    >
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      {uploading ? t('workspace.files.uploading') : t('workspace.files.upload')}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple={props.mode === 'open' && !!props.multi}
                      accept={hasAccept ? accept!.join(',') : undefined}
                      className="hidden"
                      onChange={(e) => {
                        void onUploadFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="min-h-44 flex-1 overflow-y-auto p-2">
              {newFolderOpen && !searching && (
                <div className="mx-1 mb-1 rounded-lg bg-foreground/5 p-2 ring-1 ring-border">
                  <div className="flex items-center gap-2">
                    <FolderPlus className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <input
                      autoFocus
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void createFolderHere();
                        } else if (e.key === 'Escape') {
                          e.stopPropagation();
                          setNewFolderOpen(false);
                        }
                      }}
                      placeholder={t('workspace.files.folderName')}
                      maxLength={80}
                      className="w-full bg-transparent text-sm outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => void createFolderHere()}
                      disabled={!newFolderName.trim() || creatingFolder}
                      className="shrink-0 rounded-md bg-foreground px-2.5 py-1 text-[11px] font-medium text-background disabled:opacity-50"
                    >
                      {creatingFolder ? t('agentTools.creating') : t('actions.create')}
                    </button>
                  </div>
                  {nameTaken && (
                    <div className="mt-1 pl-6 text-[10px] text-amber-600 dark:text-amber-400">
                      {t('workspace.files.nameTaken')}
                    </div>
                  )}
                </div>
              )}
              {uploadError && !uploading && (
                <div className="mx-1 mb-1 flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0">{uploadError}</span>
                </div>
              )}
              {uploading ? (
                <div className="flex min-h-40 flex-col items-center justify-center gap-2 p-6 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  <div className="w-full truncate px-4 text-xs font-medium">
                    Uploading {uploadNames.length > 1 ? `${uploadNames.length} files` : uploadNames[0]}…
                  </div>
                </div>
              ) : loading ? (
                <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
              ) : folders.length === 0 && files.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">
                  {searching
                    ? 'No matching files.'
                    : sharedScope
                    ? t('workspace.files.emptyShared')
                    : appFlat
                      ? t('workspace.files.emptyForApp')
                      : isOpenMode && hasAccept && scope === 'widget' && allFiles.length > 0
                        ? t('workspace.files.emptyFiltered')
                        : t('workspace.panels.nothingHereYet')}
                </div>
              ) : (
                <>
                  {folders.map((f) => {
                    const resolved = resolveContentKind(f);
                    const disabled = isAppDocFolder(f);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && enter(f)}
                        className={cn(rowBtn, disabled ? 'cursor-default opacity-45' : 'hover:bg-foreground/8')}
                      >
                        <ContentPreview item={f} size="sm" className="!h-6 !w-6 !rounded-md" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{f.displayName}</span>
                          <span className="block truncate text-[10px] text-muted-foreground">
                            {disabled ? `${resolved.label} · documents cannot hold items` : resolved.label}
                          </span>
                        </span>
                        {!disabled && <ChevronRight className="h-3 w-3 shrink-0 opacity-40" />}
                      </button>
                    );
                  })}
                  {files.map((it) => {
                    const on = sel.has(it.id);
                    const resolved = resolveContentKind(
                      projectIds.has(it.id)
                        ? it
                        : { contentType: it.contentType, mimeType: it.mimeType, displayName: it.displayName },
                    );
                    return (
                      <button
                        key={it.id}
                        type="button"
                        disabled={!selectableFiles}
                        onClick={() => toggle(it.id)}
                        onDoubleClick={() => {
                          if (!selectableFiles) return;
                          toggle(it.id);
                          setTimeout(confirmOpen, 0);
                        }}
                        className={cn(
                          rowBtn,
                          !selectableFiles
                            ? 'cursor-default opacity-45'
                            : on
                              ? 'bg-foreground/10 ring-1 ring-foreground/30'
                              : 'hover:bg-foreground/8',
                        )}
                      >
                        <ContentPreview item={it} size="sm" className="!h-6 !w-6 !rounded-md" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{it.displayName || it.originalName}</span>
                          <span className="block truncate text-[10px] text-muted-foreground">{resolved.label}</span>
                        </span>
                        <span className="shrink-0 text-[10px] opacity-50">{fmtSize(it.size)}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="p-4">
            <input
              autoFocus
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmOpen();
              }}
              placeholder="https://…"
              className="w-full rounded-lg bg-foreground/5 px-3 py-2 text-sm outline-none ring-1 ring-border focus:ring-foreground/30"
            />
            <div className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
              <LinkIcon className="h-3 w-3" /> {t('workspace.files.urlOnly')}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-1.5 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.4)]">
          {(isMoveMode || isSaveMode) && tab === 'file' && (
            <button
              type="button"
              onClick={() => setNewFolderOpen((v) => !v)}
              className="mr-auto inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium hover:bg-accent"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              {t('workspace.panels.newFolder')}
            </button>
          )}
          <button
            type="button"
            onClick={cancel}
            className="inline-flex h-8 items-center rounded-md px-3 text-xs font-medium hover:bg-accent"
          >
            Cancel
          </button>
          {isSaveMode ? (
            <button
              type="button"
              onClick={confirmSave}
              disabled={!name.trim()}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background hover:bg-foreground/90 disabled:opacity-60"
            >
              <FolderInput className="h-3.5 w-3.5" />
              {t('workspace.files.saveHere')}
            </button>
          ) : isMoveMode ? (
            <button
              type="button"
              onClick={confirmMove}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background hover:bg-foreground/90"
            >
              <FolderInput className="h-3.5 w-3.5" />
              {t('workspace.files.moveHere')}
            </button>
          ) : (
            <button
              type="button"
              onClick={confirmOpen}
              disabled={tab === 'file' ? sel.size === 0 : !urlValue.trim()}
              className="inline-flex h-8 items-center rounded-md bg-foreground px-3 text-xs font-medium text-background hover:bg-foreground/90 disabled:opacity-60"
            >
              {tab === 'file' ? (sel.size > 1 ? `Use ${sel.size} files` : 'Use file') : 'Use link'}
            </button>
          )}
        </div>
      </div>
    </PopoverPortal>
  );
}
